import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, CheckCircle } from 'lucide-react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { submitQuoteForm, equipmentTypes, dockHeights } from '../data/mock';
import { toast } from 'sonner';
import PostalCodeInput from './PostalCodeInput';

const QuoteForm = () => {
  const navigate = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [formData, setFormData] = useState({
    company: '',
    siret: '',
    name: '',
    email: '',
    phone: '',
    equipmentType: '',
    dockHeight: '',
    quantity: '',
    location: '',
    details: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [siretVerifying, setSiretVerifying] = useState(false);
  const [siretValid, setSiretValid] = useState(null);

  // Fonction de validation email
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Fonction de validation nom (pas de chiffres)
  const validateName = (name) => {
    const nameRegex = /^[a-zA-ZÀ-ÿ\s\-']+$/;
    return nameRegex.test(name);
  };

  // Fonction de validation téléphone français (10 chiffres, obligatoire)
  const validatePhone = (phone) => {
    if (!phone) return false; // Obligatoire
    const cleanPhone = phone.replace(/\s/g, '');
    const phoneRegex = /^0[1-9]\d{8}$/;
    return phoneRegex.test(cleanPhone);
  };

  // Fonction de validation SIRET (14 chiffres)
  const validateSiretFormat = (siret) => {
    const cleanSiret = siret.replace(/\s/g, '');
    return /^\d{14}$/.test(cleanSiret);
  };

  // Vérification SIRET avec API
  const verifySiret = async (siret) => {
    const cleanSiret = siret.replace(/\s/g, '');
    
    if (!validateSiretFormat(cleanSiret)) {
      setSiretValid(false);
      return false;
    }

    setSiretVerifying(true);
    try {
      const response = await fetch(`https://entreprise.data.gouv.fr/api/sirene/v3/etablissements/${cleanSiret}`);
      
      if (response.ok) {
        const data = await response.json();
        setSiretValid(true);
        // Optionnel : pré-remplir le nom de l'entreprise
        if (data.etablissement && data.etablissement.unite_legale) {
          const companyName = data.etablissement.unite_legale.denomination || 
                             data.etablissement.unite_legale.nom_raison_sociale;
          if (companyName && !formData.company) {
            setFormData(prev => ({ ...prev, company: companyName }));
          }
        }
        setSiretVerifying(false);
        return true;
      } else {
        setSiretValid(false);
        setSiretVerifying(false);
        return false;
      }
    } catch (error) {
      console.error('Erreur vérification SIRET:', error);
      setSiretValid(false);
      setSiretVerifying(false);
      return false;
    }
  };

  const handleSiretChange = (e) => {
    const value = e.target.value;
    setFormData({ ...formData, siret: value });
    setSiretValid(null);
  };

  const handleSiretBlur = () => {
    if (formData.siret) {
      verifySiret(formData.siret);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSelectChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation du nom (pas de chiffres)
    if (!validateName(formData.name)) {
      toast.error("Le nom ne doit pas contenir de chiffres");
      return;
    }
    
    // Validation de l'email
    if (!validateEmail(formData.email)) {
      toast.error("Veuillez saisir une adresse email valide (avec @ et nom de domaine)");
      return;
    }
    
    // Validation du téléphone (obligatoire)
    if (!formData.phone) {
      toast.error("Le numéro de téléphone est obligatoire");
      return;
    }
    
    if (!validatePhone(formData.phone)) {
      toast.error("Numéro de téléphone invalide (format français attendu : 10 chiffres commençant par 0)");
      return;
    }
    
    // Validation du SIRET (optionnel, mais si rempli doit être valide)
    if (formData.siret) {
      if (!validateSiretFormat(formData.siret)) {
        toast.error("Le SIRET doit contenir 14 chiffres");
        return;
      }
      
      if (siretValid === false) {
        toast.error("Le numéro SIRET n'existe pas ou est invalide");
        return;
      }
      
      if (siretValid === null) {
        toast.error("Veuillez attendre la vérification du SIRET");
        return;
      }
    }
    
    // Validation du lieu d'installation (obligatoire)
    if (!formData.location) {
      toast.error("Veuillez sélectionner un code postal et une ville");
      return;
    }
    
    if (!executeRecaptcha) {
      toast.error('reCAPTCHA non disponible');
      return;
    }
    
    setIsSubmitting(true);

    try {
      // Obtenir le token reCAPTCHA
      const token = await executeRecaptcha('quote_form');
      
      // Ajouter le token aux données du formulaire
      const dataWithToken = {
        ...formData,
        recaptchaToken: token
      };
      
      const result = await submitQuoteForm(dataWithToken);
      if (result.success) {
        // Rediriger vers la page de confirmation avec les données du devis
        navigate('/confirmation-devis', {
          state: {
            quoteData: {
              ...formData,
              id: result.id || `QUOTE-${Date.now()}`
            }
          }
        });
      }
    } catch (error) {
      console.error('Quote submission error:', error);
      const errorMessage = error.message || "Une erreur est survenue. Veuillez réessayer.";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-[#020d2a] to-[#03152f] rounded-xl shadow-2xl p-8 border border-orange-500/30">
      <div className="mb-6">
        <div className="inline-block bg-[#FF6600]/20 text-[#FF6600] px-3 py-1 rounded-full text-sm font-semibold mb-3 border border-[#FF6600]/30">
          Devis détaillé
        </div>
        <h3 className="text-2xl font-bold text-white mb-2">
          Demande de Devis Personnalisé
        </h3>
        <p className="text-gray-300">
          Remplissez ce formulaire pour recevoir un devis sur mesure sous 48h.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <label htmlFor="company" className="block text-sm font-semibold text-white mb-2">
              Société <span className="text-orange-500">*</span>
            </label>
            <Input
              id="company"
              name="company"
              type="text"
              required
              value={formData.company}
              onChange={handleChange}
              placeholder="Nom de votre société"
              className="w-full bg-#020d2a border-gray-700 text-white placeholder:text-gray-400"
            />
          </div>

          <div>
            <label htmlFor="siret" className="block text-sm font-semibold text-white mb-2">
              SIRET <span className="text-gray-500">(optionnel)</span>
            </label>
            <div className="relative">
              <Input
                id="siret"
                name="siret"
                type="text"
                value={formData.siret}
                onChange={handleSiretChange}
                onBlur={handleSiretBlur}
                placeholder="14 chiffres"
                maxLength={14}
                className={`w-full bg-#020d2a border-gray-700 text-white placeholder:text-gray-400 ${
                  siretValid === true ? 'border-green-500' : 
                  siretValid === false ? 'border-red-500' : ''
                }`}
              />
              {siretVerifying && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="animate-spin h-4 w-4 border-2 border-orange-500 border-t-transparent rounded-full"></div>
                </div>
              )}
              {siretValid === true && !siretVerifying && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500">
                  ✓
                </div>
              )}
              {siretValid === false && !siretVerifying && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500">
                  ✗
                </div>
              )}
            </div>
            {siretValid === true && (
              <p className="text-xs text-green-500 mt-1">SIRET valide</p>
            )}
            {siretValid === false && (
              <p className="text-xs text-red-500 mt-1">SIRET invalide ou inexistant</p>
            )}
            <p className="text-xs text-gray-400 mt-1">Si rempli, sera vérifié automatiquement</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <label htmlFor="name" className="block text-sm font-semibold text-white mb-2">
              Nom complet <span className="text-orange-500">*</span>
            </label>
            <Input
              id="name"
              name="name"
              type="text"
              required
              value={formData.name}
              onChange={handleChange}
              placeholder="Votre nom"
              className="w-full bg-#020d2a border-gray-700 text-white placeholder:text-gray-400"
            />
            <p className="text-xs text-gray-400 mt-1">
              Lettres uniquement (pas de chiffres)
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <label htmlFor="email" className="block text-sm font-semibold text-white mb-2">
              Email <span className="text-orange-500">*</span>
            </label>
            <Input
              id="email"
              name="email"
              type="email"
              required
              value={formData.email}
              onChange={handleChange}
              placeholder="votre@email.fr"
              className="w-full bg-#020d2a border-gray-700 text-white placeholder:text-gray-400"
            />
            <p className="text-xs text-gray-400 mt-1">
              Format valide requis (avec @ et domaine)
            </p>
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-semibold text-white mb-2">
              Téléphone <span className="text-orange-500">*</span>
            </label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              required
              value={formData.phone}
              onChange={handleChange}
              placeholder="0X XX XX XX XX"
              className="w-full bg-#020d2a border-gray-700 text-white placeholder:text-gray-400"
            />
            <p className="text-xs text-gray-400 mt-1">
              Format français : 10 chiffres commençant par 0
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <label htmlFor="equipmentType" className="block text-sm font-semibold text-white mb-2">
              Type d'équipement <span className="text-gray-500">(optionnel)</span>
            </label>
            <Select 
              value={formData.equipmentType} 
              onValueChange={(value) => handleSelectChange('equipmentType', value)}
            >
              <SelectTrigger className="w-full bg-#020d2a border-gray-700 text-white">
                <SelectValue placeholder="Sélectionnez un équipement" />
              </SelectTrigger>
              <SelectContent>
                {equipmentTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label htmlFor="dockHeight" className="block text-sm font-semibold text-white mb-2">
              Hauteur de quai <span className="text-gray-500">(optionnel)</span>
            </label>
            <Select 
              value={formData.dockHeight} 
              onValueChange={(value) => handleSelectChange('dockHeight', value)}
            >
              <SelectTrigger className="w-full bg-#020d2a border-gray-700 text-white">
                <SelectValue placeholder="Sélectionnez la hauteur" />
              </SelectTrigger>
              <SelectContent>
                {dockHeights.map((height) => (
                  <SelectItem key={height.value} value={height.value}>
                    {height.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <label htmlFor="quantity" className="block text-sm font-semibold text-white mb-2">
              Quantité <span className="text-gray-500">(optionnel)</span>
            </label>
            <Input
              id="quantity"
              name="quantity"
              type="number"
              min="1"
              value={formData.quantity}
              onChange={handleChange}
              placeholder="Nombre d'unités"
              className="w-full bg-#020d2a border-gray-700 text-white placeholder:text-gray-400"
            />
          </div>

          <div>
            <label htmlFor="location" className="block text-sm font-semibold text-white mb-2">
              Lieu d'installation <span className="text-orange-500">*</span>
            </label>
            <PostalCodeInput
              value={formData.location}
              onChange={(value) => setFormData({ ...formData, location: value })}
              onCitySelect={(value) => setFormData({ ...formData, location: value })}
            />
            <p className="text-xs text-gray-400 mt-1">
              Tapez votre code postal pour voir les villes
            </p>
          </div>
        </div>

        <div>
          <label htmlFor="details" className="block text-sm font-semibold text-white mb-2">
            Détails complémentaires
          </label>
          <Textarea
            id="details"
            name="details"
            value={formData.details}
            onChange={handleChange}
            placeholder="Précisez vos besoins, contraintes particulières, délais souhaités..."
            rows={4}
            className="w-full bg-#020d2a border-gray-700 text-white placeholder:text-gray-400"
          />
        </div>

        <div className="text-xs text-gray-400 text-center">
          Ce site est protégé par reCAPTCHA. La <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:underline">politique de confidentialité</a> et les <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:underline">conditions d'utilisation</a> de Google s'appliquent.
        </div>

        <Button 
          type="submit" 
          disabled={isSubmitting}
          className="w-full bg-orange-500 hover:bg-orange-600 text-white text-lg py-6"
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              Envoi en cours...
            </>
          ) : (
            <>
              <FileText className="w-5 h-5 mr-2" />
              Recevoir mon devis gratuit
            </>
          )}
        </Button>

        <p className="text-gray-400 text-sm text-center">
          Réponse sous 24h ouvrées • Devis gratuit et sans engagement
        </p>
      </form>
    </div>
  );
};

export default QuoteForm;
